// Thin façade over sdk-core 14 for the escrow contract: queries return plain
// JS values, write builders return UNSIGNED transactions (the dApp signs them
// via the connected DappProvider through auth.ts, then broadcasts via the API
// provider). All addresses/amounts as plain strings/bigints at the boundary.

import {
  Address,
  AbiRegistry,
  ApiNetworkProvider,
  SmartContractController,
  SmartContractTransactionsFactory,
  TransactionsFactoryConfig,
  Token,
  TokenTransfer,
  type Transaction,
} from "@multiversx/sdk-core";

import escrowAbiJson from "./abi/escrow.abi.json";
import { NETWORK_URLS, type AppConfig } from "./config";
import { getProvider } from "./auth";

const CHAIN_IDS: Record<string, string> = {
  mainnet: "1",
  testnet: "T",
  devnet: "D",
};

const GAS_CALL = 30_000_000n;

let _api: ApiNetworkProvider | null = null;
let _ctrl: SmartContractController | null = null;
let _factory: SmartContractTransactionsFactory | null = null;
let _abi: AbiRegistry | null = null;

function abi(): AbiRegistry {
  if (!_abi) _abi = AbiRegistry.create(escrowAbiJson as any);
  return _abi;
}

export function api(cfg: AppConfig): ApiNetworkProvider {
  if (!_api) _api = new ApiNetworkProvider(NETWORK_URLS[cfg.network].api);
  return _api;
}

function ctrl(cfg: AppConfig): SmartContractController {
  if (!_ctrl) {
    _ctrl = new SmartContractController({
      chainID: CHAIN_IDS[cfg.network],
      networkProvider: api(cfg),
      abi: abi(),
    });
  }
  return _ctrl;
}

function factory(cfg: AppConfig): SmartContractTransactionsFactory {
  if (!_factory) {
    const config = new TransactionsFactoryConfig({ chainID: CHAIN_IDS[cfg.network] });
    _factory = new SmartContractTransactionsFactory({ config, abi: abi() });
  }
  return _factory;
}

const addr = (s: string) => Address.newFromBech32(s);
const bech = (a: unknown): string =>
  typeof a === "string" ? a : (a as any)?.toBech32 ? (a as any).toBech32() : String(a);

// sdk-core wraps variadic return values inside a single nested array, even
// when the variadic is empty. So `getPoolCreators` on an empty pair comes back
// as `[[]]` not `[]` — and `.map(bech)` over `[[]]` yields `[""]` (because
// `String([])` is the empty string). Then we hand that empty string to
// Address.newFromBech32 and get "Cannot create address from:" — exactly the
// crash. Unwrap + drop empty placeholders here.
function unwrapVariadic(r: any): unknown[] {
  if (!Array.isArray(r)) return [];
  if (r.length === 1 && Array.isArray(r[0])) return r[0];
  return r;
}

// Robust coercion to bigint. sdk-core's ABI parser returns BigUint values
// as bignumber.js instances; their .valueOf() can yield scientific notation
// ("9.818e+21") which BigInt(...) rejects. .toFixed(0) gives the full integer.
function bn(v: any): bigint {
  if (v == null) return 0n;
  if (typeof v === "bigint") return v;
  if (typeof v === "number") return BigInt(Math.trunc(v));
  if (typeof v.toFixed === "function") return BigInt(v.toFixed(0));
  const s = String(v);
  if (/^-?\d+$/.test(s)) return BigInt(s);
  return BigInt(Math.trunc(Number(s)));
}

// ─── reads ───────────────────────────────────────────────────────────
async function q(cfg: AppConfig, fn: string, args: any[] = []): Promise<any[]> {
  return await ctrl(cfg).query({
    contract: addr(cfg.contractAddress),
    function: fn,
    arguments: args,
  });
}

export async function getPairCount(cfg: AppConfig): Promise<number> {
  const r = await q(cfg, "getPairCount");
  return Number(r[0]);
}

// (v2: no global platform fee / min swap any more — they live per-pair.)

export type EscrowPair = {
  id: number;
  stakedToken: string;
  freeToken: string;
  publicPools: boolean;
  active: boolean;
  platformFeeBps: number;
  minSwapAmount: bigint;
};

export async function getPair(cfg: AppConfig, pairId: number): Promise<EscrowPair> {
  const r = await q(cfg, "getPair", [pairId]);
  const p = r[0];
  return {
    id: pairId,
    stakedToken: String(p.staked_token),
    freeToken: String(p.free_token),
    publicPools: !!p.public_pools,
    active: !!p.active,
    platformFeeBps: Number(p.platform_fee_bps),
    minSwapAmount: bn(p.min_swap_amount),
  };
}

export async function getAllPairs(cfg: AppConfig): Promise<EscrowPair[]> {
  // pair_count is strictly monotonic — removePair clears the slot but keeps
  // the counter so the deleted id appears as a gap. Iterate 1..count and
  // skip any id that reverts (= removed).
  const count = await getPairCount(cfg);
  const ids = Array.from({ length: count }, (_, i) => i + 1);
  const results = await Promise.allSettled(ids.map((id) => getPair(cfg, id)));
  return results
    .filter((r): r is PromiseFulfilledResult<EscrowPair> => r.status === "fulfilled")
    .map((r) => r.value);
}

export async function getPoolCreators(cfg: AppConfig, pairId: number): Promise<string[]> {
  const r = await q(cfg, "getPoolCreators", [pairId]);
  return unwrapVariadic(r)
    .map(bech)
    .filter((s) => s.length > 0);
}

export type Pool = {
  exists: boolean;
  discountBps: number;
  freeBalance: bigint;
  // Snapshotted from the pair at creation — immutable for the pool's life.
  platformFeeBps: number;
  minSwapAmount: bigint;
};

export async function getPool(cfg: AppConfig, pairId: number, creator: string): Promise<Pool> {
  const r = await q(cfg, "getPool", [pairId, addr(creator)]);
  const p = r[0];
  return {
    exists: !!p.exists,
    discountBps: Number(p.discount_bps),
    freeBalance: bn(p.free_balance),
    platformFeeBps: Number(p.platform_fee_bps),
    minSwapAmount: bn(p.min_swap_amount),
  };
}

export type Quote = {
  stakedIn: bigint;
  payout: bigint;
  platformFee: bigint;
  poolMargin: bigint;
  discountBps: number;
  platformFeeBps: number;
};

export async function getQuote(
  cfg: AppConfig,
  pairId: number,
  poolCreator: string,
  amount: bigint,
): Promise<Quote> {
  const r = await q(cfg, "getQuote", [pairId, addr(poolCreator), amount]);
  const x = r[0];
  return {
    stakedIn: bn(x.staked_in),
    payout: bn(x.payout),
    platformFee: bn(x.platform_fee),
    poolMargin: bn(x.pool_margin),
    discountBps: Number(x.discount_bps),
    platformFeeBps: Number(x.platform_fee_bps),
  };
}

// ─── writes — unsigned tx builders ───────────────────────────────────
async function fetchNonce(cfg: AppConfig, address: string): Promise<bigint> {
  const acc = await api(cfg).getAccount(addr(address));
  return BigInt(acc.nonce);
}

async function withNonce(cfg: AppConfig, sender: string, tx: Transaction): Promise<Transaction> {
  tx.nonce = await fetchNonce(cfg, sender);
  return tx;
}

export async function buildSwap(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  poolCreator: string,
  stakedTokenId: string,
  stakedNonce: number,
  amount: bigint,
  minOut: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "swap",
    gasLimit: GAS_CALL,
    arguments: [pairId, addr(poolCreator), minOut],
    tokenTransfers: [
      new TokenTransfer({
        token: new Token({ identifier: stakedTokenId, nonce: BigInt(stakedNonce) }),
        amount,
      }),
    ],
  }));
}

export async function buildCreatePool(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  freeTokenId: string,
  discountBps: number,
  initialAmount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "createPool",
    gasLimit: GAS_CALL,
    arguments: [pairId, discountBps],
    tokenTransfers:
      initialAmount > 0n
        ? [new TokenTransfer({
            token: new Token({ identifier: freeTokenId }),
            amount: initialAmount,
          })]
        : [],
  }));
}

export async function buildFundPool(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  freeTokenId: string,
  amount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "fundPool",
    gasLimit: GAS_CALL,
    arguments: [pairId],
    tokenTransfers: [
      new TokenTransfer({
        token: new Token({ identifier: freeTokenId }),
        amount,
      }),
    ],
  }));
}

export async function buildWithdrawFree(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  amount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "withdrawFree",
    gasLimit: GAS_CALL,
    arguments: [pairId, amount],
  }));
}

export async function buildClosePool(
  cfg: AppConfig,
  sender: string,
  pairId: number,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "closePool",
    gasLimit: GAS_CALL,
    arguments: [pairId],
  }));
}

// admin
export async function buildAddPair(
  cfg: AppConfig,
  sender: string,
  stakedToken: string,
  freeToken: string,
  publicPools: boolean,
  platformFeeBps: number,
  minSwapAmount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "addPair",
    gasLimit: GAS_CALL,
    arguments: [stakedToken, freeToken, publicPools, platformFeeBps, minSwapAmount],
  }));
}

export async function buildSetPairPlatformFee(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  bps: number,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "setPairPlatformFee",
    gasLimit: GAS_CALL,
    arguments: [pairId, bps],
  }));
}

export async function buildSetPairMinSwap(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  amount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "setPairMinSwap",
    gasLimit: GAS_CALL,
    arguments: [pairId, amount],
  }));
}

export async function buildRemovePair(
  cfg: AppConfig,
  sender: string,
  pairId: number,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "removePair",
    gasLimit: GAS_CALL,
    arguments: [pairId],
  }));
}

export async function buildForceClosePool(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  creator: string,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "forceClosePool",
    gasLimit: GAS_CALL,
    arguments: [pairId, addr(creator)],
  }));
}

export async function buildSetPairPublic(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  publicPools: boolean,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "setPairPublic",
    gasLimit: GAS_CALL,
    arguments: [pairId, publicPools],
  }));
}

export async function buildSetPairActive(
  cfg: AppConfig,
  sender: string,
  pairId: number,
  active: boolean,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "setPairActive",
    gasLimit: GAS_CALL,
    arguments: [pairId, active],
  }));
}

// ─── emergencyWithdraw — owner-only, drains ANY tokens the contract holds ───
export type Holding = {
  token: string;     // identifier, e.g. "FOXSY-583905" or "SFOXSY-f7a2e6"
  nonce: number;     // 0 for fungible, >0 for Meta-ESDT/SFT/NFT
  balance: bigint;   // raw integer balance
};

/** Fetch everything the contract currently holds (fungible + meta/SFT/NFT) so
 *  the admin can pick what to drain. The API returns balances as strings; we
 *  hand back integers to keep the boundary uniform. */
export async function getContractHoldings(cfg: AppConfig): Promise<Holding[]> {
  const base = NETWORK_URLS[cfg.network].api;
  const addr = cfg.contractAddress;
  const out: Holding[] = [];

  // Fungible ESDTs.
  try {
    const r = await fetch(`${base}/accounts/${addr}/tokens?size=1000`);
    if (r.ok) {
      const arr = (await r.json()) as Array<{ identifier: string; balance: string }>;
      for (const t of arr) {
        const bal = BigInt(t.balance ?? "0");
        if (bal > 0n) out.push({ token: t.identifier, nonce: 0, balance: bal });
      }
    }
  } catch { /* ignore — empty list is fine */ }

  // Meta-ESDT / SFT / NFT positions.
  try {
    const r = await fetch(`${base}/accounts/${addr}/nfts?size=1000`);
    if (r.ok) {
      const arr = (await r.json()) as Array<{ collection: string; nonce: number; balance: string }>;
      for (const n of arr) {
        const bal = BigInt(n.balance ?? "0");
        if (bal > 0n) out.push({ token: n.collection, nonce: n.nonce, balance: bal });
      }
    }
  } catch { /* ignore */ }

  return out;
}

export async function buildEmergencyWithdraw(
  cfg: AppConfig,
  sender: string,
  payments: Holding[],
): Promise<Transaction> {
  // The endpoint takes a variadic of (TokenIdentifier, u64, BigUint) triplets.
  // sdk-core's typed-values builder handles encoding from the ABI, so we just
  // flatten the list into the arguments slot.
  const args: any[] = [];
  for (const p of payments) {
    args.push(p.token, p.nonce, p.balance);
  }
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "emergencyWithdraw",
    // Wiping all pools across all pairs is potentially the heaviest op the
    // contract supports — give it room. (testnet ceiling is 600m).
    gasLimit: 200_000_000n,
    arguments: args,
  }));
}

export async function buildSetFeeCollector(
  cfg: AppConfig,
  sender: string,
  collector: string,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(cfg.contractAddress),
    function: "setFeeCollector",
    gasLimit: GAS_CALL,
    arguments: [addr(collector)],
  }));
}

// ─── sign + broadcast ────────────────────────────────────────────────
export async function signAndSend(cfg: AppConfig, tx: Transaction): Promise<string> {
  const provider = getProvider();
  if (!provider) throw new Error("wallet not connected");
  const [signed] = await provider.signTransactions([tx]);
  const hash = await api(cfg).sendTransaction(signed);
  return hash;
}

export async function awaitCompleted(cfg: AppConfig, hash: string): Promise<unknown> {
  return await api(cfg).awaitTransactionCompleted(hash);
}

// ─── on-chain config (no .env needed beyond the contract address) ───
/** Fetch the contract's on-chain owner. Single source of truth — the wallet
 *  compared against this is what gates the admin panel. */
export async function getContractOwner(cfg: AppConfig): Promise<string> {
  const resp = await fetch(
    `${NETWORK_URLS[cfg.network].api}/accounts/${cfg.contractAddress}`,
  );
  if (!resp.ok) {
    throw new Error(`Failed to fetch contract account: HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as { ownerAddress?: string };
  return data.ownerAddress ?? "";
}

// ─── token-balance helpers (for the dashboard "your wallet" panel) ───
export async function getOwnedSftPositions(
  cfg: AppConfig,
  address: string,
  stakedTokenId: string,
): Promise<Array<{ nonce: number; balance: bigint }>> {
  if (!address || !stakedTokenId) return [];
  const resp = await fetch(
    `${NETWORK_URLS[cfg.network].api}/accounts/${address}/nfts?collections=${stakedTokenId}&size=100`,
  );
  if (!resp.ok) return [];
  const arr = (await resp.json()) as Array<{ nonce: number; balance: string }>;
  return arr.map((n) => ({ nonce: n.nonce, balance: BigInt(n.balance) }));
}

export async function getFungibleBalance(
  cfg: AppConfig,
  address: string,
  tokenId: string,
): Promise<bigint> {
  if (!address || !tokenId) return 0n;
  // /accounts/{addr}/tokens/{id} returns 404 (with red console error) when
  // the wallet holds zero. The list-with-filter form returns 200 + an empty
  // array in that case — silent on the console.
  try {
    const resp = await fetch(
      `${NETWORK_URLS[cfg.network].api}/accounts/${address}/tokens?identifier=${tokenId}&size=1`,
    );
    if (!resp.ok) return 0n;
    const arr = (await resp.json()) as Array<{ balance: string }>;
    return arr.length > 0 ? BigInt(arr[0].balance ?? "0") : 0n;
  } catch {
    return 0n;
  }
}

// ─── formatting helpers ──────────────────────────────────────────────
export function fmtAmount(wei: bigint, decimals = 18, fractionDigits = 4): string {
  const neg = wei < 0n;
  const abs = neg ? -wei : wei;
  const divisor = 10n ** BigInt(decimals);
  const whole = abs / divisor;
  const frac = abs % divisor;
  const fracStr = frac.toString().padStart(decimals, "0").slice(0, fractionDigits).replace(/0+$/, "");
  const s = fracStr ? `${whole}.${fracStr}` : `${whole}`;
  return neg ? `-${s}` : s;
}

export function parseAmount(s: string, decimals = 18): bigint {
  const cleaned = s.trim().replace(",", ".");
  if (!cleaned) return 0n;
  const [whole, frac = ""] = cleaned.split(".");
  const fracPadded = (frac + "0".repeat(decimals)).slice(0, decimals);
  return BigInt(whole || "0") * 10n ** BigInt(decimals) + BigInt(fracPadded || "0");
}

export function bpsToPct(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}
